<template>
  <!--
    Practo-style search: location first, then doctors/clinics/specialities.
    Picking a suggestion searches straight away; Enter searches the typed text.
  -->
  <form class="yd-sb" :class="{ 'yd-sb--compact': compact }" role="search" @submit.prevent="submit">
    <!-- Location -->
    <div class="yd-sb__field yd-sb__field--location">
      <i class="feather-map-pin yd-sb__icon" aria-hidden="true"></i>
      <input
        ref="locationInput"
        v-model="area"
        type="text"
        class="yd-sb__input"
        role="combobox"
        autocomplete="off"
        :maxlength="cityNameMaxLength"
        :placeholder="locating ? $t('city_locating') : $t('home_srchdoc_placeholder2')"
        :aria-label="$t('home_srchdoc_placeholder2')"
        :aria-expanded="openPanel === 'location' ? 'true' : 'false'"
        :aria-controls="ids.location"
        :aria-activedescendant="activeOptionId('location')"
        @focus="open('location')"
        @blur="close('location')"
        @input="onLocationInput"
        @keydown="onKeydown('location', $event)"
      />
      <button
        v-if="area"
        type="button"
        class="yd-sb__clear"
        :aria-label="$t('search_clear_city')"
        @mousedown.prevent
        @click="clearCity"
      >
        <i class="feather-x" aria-hidden="true"></i>
      </button>

      <ul v-if="openPanel === 'location'" :id="ids.location" class="yd-sb__menu" role="listbox">
        <li
          v-for="(option, index) in locationOptions"
          :id="optionId('location', index)"
          :key="option.key"
          role="option"
          :aria-selected="activeIndex === index ? 'true' : 'false'"
          :class="['yd-sb__option', `yd-sb__option--${option.kind}`, { 'is-active': activeIndex === index }]"
          @mousedown.prevent
          @mouseenter="activeIndex = index"
          @click="chooseLocation(option)"
        >
          <template v-if="option.kind === 'locate'">
            <i :class="locating ? 'fas fa-spinner fa-spin' : 'feather-crosshair'" aria-hidden="true"></i>
            {{ locating ? $t("city_locating") : $t("city_use_my_location") }}
          </template>
          <template v-else-if="option.kind === 'all'">{{ $t("search_all_cities") }}</template>
          <template v-else>
            <span class="yd-sb__badge" aria-hidden="true"><i :class="option.icon || 'feather-search'"></i></span>
            <span class="yd-sb__text">
              <span class="yd-sb__title">
                <span v-for="(part, i) in highlight(option.label, area)" :key="i" :class="{ 'yd-sb__match': part.match }">{{ part.text }}</span>
              </span>
              <span v-if="option.sub" class="yd-sb__sub">{{ option.sub }}</span>
            </span>
            <span v-if="option.typeLabel" class="yd-sb__type">{{ $t(option.typeLabel) }}</span>
          </template>
        </li>
        <li v-if="placesSearching && !placeOptions.length" class="yd-sb__status">{{ $t("location_searching") }}</li>
        <li v-else-if="citiesLoading && !typedPlaceQuery" class="yd-sb__status">{{ $t("city_loading") }}</li>
        <li v-else-if="citiesError && !typedPlaceQuery" class="yd-sb__status yd-sb__status--error">{{ citiesError }}</li>
        <li v-else-if="area && locationOptions.length <= 2 && !placesSearching" class="yd-sb__status">{{ $t("city_no_results") }}</li>
      </ul>
    </div>

    <!-- Doctors, clinics, specialities -->
    <div class="yd-sb__field yd-sb__field--query">
      <i class="feather-search yd-sb__icon" aria-hidden="true"></i>
      <input
        ref="queryInput"
        v-model="text"
        type="text"
        class="yd-sb__input"
        role="combobox"
        autocomplete="off"
        enterkeyhint="search"
        :placeholder="$t('home_srchdoc_placeholder1')"
        :aria-label="$t('home_srchdoc_placeholder1')"
        :aria-expanded="openPanel === 'query' ? 'true' : 'false'"
        :aria-controls="ids.query"
        :aria-activedescendant="activeOptionId('query')"
        @focus="open('query')"
        @blur="close('query')"
        @input="onQueryInput"
        @keydown="onKeydown('query', $event)"
      />

      <ul v-if="openPanel === 'query'" :id="ids.query" class="yd-sb__menu" role="listbox">
        <li v-if="!text.trim()" class="yd-sb__heading">{{ $t("search_popular") }}</li>
        <li
          v-for="(option, index) in queryOptions"
          :id="optionId('query', index)"
          :key="option.key"
          role="option"
          :aria-selected="activeIndex === index ? 'true' : 'false'"
          :class="['yd-sb__option', { 'is-active': activeIndex === index }]"
          @mousedown.prevent
          @mouseenter="activeIndex = index"
          @click="chooseQuery(option)"
        >
          <span class="yd-sb__badge" aria-hidden="true"><i :class="option.icon"></i></span>
          <span class="yd-sb__text">
            <span class="yd-sb__title">
              <span v-for="(part, i) in highlight(option.label, text)" :key="i" :class="{ 'yd-sb__match': part.match }">{{ part.text }}</span>
            </span>
          </span>
          <span class="yd-sb__type">{{ $t(option.typeLabel) }}</span>
        </li>
        <li v-if="text.trim() && !queryOptions.length" class="yd-sb__status">
          {{ $t("search_no_results", { query: text.trim() }) }}
        </li>
      </ul>
    </div>

    <button type="submit" class="yd-sb__submit">
      <i class="feather-search" aria-hidden="true"></i>
      <span>{{ $t("home_srchdoc_searchbtn") }}</span>
    </button>

    <p v-if="message" class="yd-sb__message" role="alert">{{ message }}</p>
  </form>
</template>

<script>
import axios from "axios";
import specializations from "@/assets/json/specialization_settings_doctor.json";
import {
  CITY_NAME_MAX_LENGTH,
  PLACE_QUERY_MIN_LENGTH,
  cleanCityName,
  fetchNearestCity,
  fetchNearestPlace,
  fetchPlaceSuggestions,
  findCityByName,
  getCurrentPosition,
  isAllCities,
  normalizeCityName,
  placeToLastCity,
  readLastCity,
  saveLastCity
} from "@/services/cities";

// Wait for a pause in typing before asking the API.
const PLACE_SUGGEST_DELAY_MS = 200;

const PLACE_KINDS = {
  city: { icon: "feather-map-pin", typeLabel: "location_type_city" },
  locality: { icon: "feather-navigation", typeLabel: "location_type_locality" }
};

// "Use my location" / "Search in all cities" always head the location menu.
const LOCATION_ACTION_COUNT = 2;

// Shown when the search box is focused but empty.
const POPULAR_SPECIALITIES = ["General Medicine", "Cardiology", "Dentist", "Orthopedic", "ENT"];

const SUGGESTION_LIMITS = { speciality: 5, doctor: 5, clinic: 3 };

const SUGGESTION_KINDS = {
  speciality: { icon: "fas fa-stethoscope", typeLabel: "home_srchdoc_suggestion_speciality" },
  doctor: { icon: "fas fa-user-md", typeLabel: "home_srchdoc_suggestion_doctor" },
  clinic: { icon: "fas fa-hospital", typeLabel: "home_srchdoc_suggestion_clinic" }
};

let instanceCount = 0;

/** Names matching `query`, names that start with it first. */
function rankMatches(items, query, getName) {
  const starts = [];
  const contains = [];
  items.forEach(item => {
    const name = (getName(item) || "").toLowerCase();
    if (name.startsWith(query)) starts.push(item);
    else if (name.includes(query)) contains.push(item);
  });
  return [...starts, ...contains];
}

export default {
  name: "DoctorSearchBar",
  props: {
    // The current search ({ filter, city, city_id }) - the route query on the results page.
    initialQuery: { type: Object, default: () => ({}) },
    // Smaller inputs for page headers.
    compact: { type: Boolean, default: false },
    // Prefill the last used city when initialQuery has none. Off on the results
    // page, where the bar must show exactly what the results are filtered by.
    rememberCity: { type: Boolean, default: true }
  },
  data() {
    instanceCount += 1;
    return {
      ids: { location: `yd-sb-location-${instanceCount}`, query: `yd-sb-query-${instanceCount}` },
      area: "",
      selectedCityId: null,
      // The gazetteer place picked in the location box (city or locality), if any.
      selectedPlace: null,
      // GET /places/suggest results for what is typed in the location box.
      places: [],
      placesSearching: false,
      text: "",
      openPanel: null,
      activeIndex: -1,
      locating: false,
      message: "",
      cityNameMaxLength: CITY_NAME_MAX_LENGTH,
      doctors: [],
      clinics: []
    };
  },
  computed: {
    cities() {
      return this.$store.getters.getCities;
    },
    citiesLoading() {
      return this.$store.getters.isCitiesLoading;
    },
    citiesError() {
      return this.$store.getters.getCitiesError;
    },
    cityMatches() {
      const query = normalizeCityName(this.area);
      // A picked city shows the full list again, like an unfiltered dropdown.
      if (!query || this.selectedCityId || this.selectedPlace || isAllCities(query)) return this.cities;
      return rankMatches(this.cities, query, city => city.name);
    },
    // What to ask /places/suggest for, or "" while nothing new is typed.
    typedPlaceQuery() {
      const query = this.area.trim();
      if (this.selectedCityId || this.selectedPlace || isAllCities(query)) return "";
      return query.length >= PLACE_QUERY_MIN_LENGTH ? query : "";
    },
    placeOptions() {
      if (!this.typedPlaceQuery) return [];
      // The country is only spelled out abroad (PLACES_PROVIDER=google with
      // GOOGLE_PLACES_REGIONS allowing other countries).
      const abroad = place => (place.country && place.country !== "India" ? place.country : null);
      return this.places.map(place => ({
        key: `place-${place.place_id}`,
        kind: "place",
        label: place.name,
        sub: place.kind === "locality"
          ? [[place.city_name || place.district, place.state, abroad(place)].filter(Boolean).join(", "), place.pincode]
            .filter(Boolean).join(" · ")
          : [place.district !== place.name ? place.district : null, place.state, abroad(place)]
            .filter(Boolean).join(", "),
        ...PLACE_KINDS[place.kind],
        place
      }));
    },
    locationOptions() {
      const actions = [
        { key: "locate", kind: "locate" },
        { key: "all", kind: "all" }
      ];
      // Typed text: every city and locality from the gazetteer. The served
      // cities list stays as the fallback when the API has nothing (or the
      // gazetteer has not been imported yet).
      if (this.placeOptions.length) return [...actions, ...this.placeOptions];
      if (this.typedPlaceQuery && this.placesSearching) return actions;
      return [
        ...actions,
        ...this.cityMatches.map(city => ({
          key: `city-${city.city_id}`,
          kind: "city",
          label: city.name,
          sub: [city.state, city.country].filter(Boolean).join(", "),
          city
        }))
      ];
    },
    queryOptions() {
      const query = this.text.trim().toLowerCase();
      const toOption = (kind, label) => ({ key: `${kind}-${label}`, kind, label, ...SUGGESTION_KINDS[kind] });
      if (!query) return POPULAR_SPECIALITIES.map(name => toOption("speciality", name));

      const specialities = rankMatches(specializations, query, item => item.Title)
        .slice(0, SUGGESTION_LIMITS.speciality).map(item => toOption("speciality", item.Title));
      const doctors = rankMatches(this.doctors, query, name => name)
        .slice(0, SUGGESTION_LIMITS.doctor).map(name => toOption("doctor", name));
      const clinics = rankMatches(this.clinics, query, name => name)
        .slice(0, SUGGESTION_LIMITS.clinic).map(name => toOption("clinic", name));
      return [...specialities, ...doctors, ...clinics];
    },
    activeOptions() {
      if (this.openPanel === "location") return this.locationOptions;
      if (this.openPanel === "query") return this.queryOptions;
      return [];
    }
  },
  watch: {
    initialQuery: {
      deep: true,
      handler() {
        this.applyInitialQuery();
      }
    }
  },
  created() {
    // Not reactive: the pending debounce and the in-flight suggest request.
    this.suggestTimer = null;
    this.suggestController = null;
    this.applyInitialQuery();
  },
  mounted() {
    this.$store.dispatch("loadCities");
    this.fetchNames();
  },
  beforeUnmount() {
    this.cancelSuggest();
  },
  methods: {
    applyInitialQuery() {
      const { filter, city, city_id: cityId, locality, place_id: placeId } = this.initialQuery || {};
      this.text = filter || "";
      // No city given (e.g. the home page): fall back to the remembered one.
      const picked = city
        ? { name: city, city_id: cityId, locality, place_id: placeId }
        : (this.rememberCity ? readLastCity() : null);
      this.restoreLocation(picked);
    },
    /** Show a city/locality chosen earlier ({ name, city_id, place_id, locality }). */
    restoreLocation(picked) {
      if (!picked) {
        this.area = "";
        this.selectedCityId = null;
        this.selectedPlace = null;
        return;
      }
      const label = picked.locality ? `${picked.locality}, ${picked.name}` : picked.name;
      this.area = label;
      this.selectedCityId = picked.city_id ? Number(picked.city_id) : null;
      this.selectedPlace = picked.place_id
        ? {
            // A number for our own places, Google's "ChIJ..." string otherwise.
            place_id: picked.place_id,
            kind: picked.locality ? "locality" : "city",
            name: picked.locality || picked.name,
            city_name: picked.name,
            city_id: this.selectedCityId,
            label
          }
        : null;
    },
    async fetchNames() {
      try {
        const response = await axios.get("/doctors/namelist");
        this.doctors = [...new Set(response.data.doctors.map(doctor => doctor.name).filter(Boolean))];
        this.clinics = [...new Set(response.data.clinics.map(clinic => clinic.name).filter(Boolean))];
      } catch (error) {
        console.error("Error fetching doctor and clinic names:", error);
      }
    },

    // --- dropdown state -------------------------------------------------------
    open(panel) {
      this.message = "";
      this.openPanel = panel;
      this.activeIndex = -1;
    },
    close(panel) {
      // Options use mousedown.prevent, so a blur only happens on a real focus change.
      if (this.openPanel === panel) this.openPanel = null;
    },
    optionId(panel, index) {
      return `${this.ids[panel]}-${index}`;
    },
    activeOptionId(panel) {
      return this.openPanel === panel && this.activeIndex >= 0 ? this.optionId(panel, this.activeIndex) : null;
    },
    onLocationInput() {
      // A typed city no longer matches whatever was previously picked.
      this.selectedCityId = null;
      this.selectedPlace = null;
      this.openPanel = "location";
      this.queueSuggest();
      this.selectFirstLocation();
    },
    // Skip the action rows so Enter picks the best matching place.
    selectFirstLocation() {
      this.activeIndex = this.area.trim() && this.locationOptions.length > LOCATION_ACTION_COUNT
        ? LOCATION_ACTION_COUNT
        : -1;
    },
    cancelSuggest() {
      clearTimeout(this.suggestTimer);
      if (this.suggestController) this.suggestController.abort();
      this.suggestController = null;
    },
    queueSuggest() {
      this.cancelSuggest();
      if (!this.typedPlaceQuery) {
        this.places = [];
        this.placesSearching = false;
        return;
      }
      this.placesSearching = true;
      this.suggestTimer = setTimeout(this.loadSuggestions, PLACE_SUGGEST_DELAY_MS);
    },
    async loadSuggestions() {
      const query = this.typedPlaceQuery;
      if (!query) return;
      const controller = new AbortController();
      this.suggestController = controller;
      let places = [];
      try {
        places = await fetchPlaceSuggestions(query, { signal: controller.signal });
      } catch (error) {
        // Superseded by newer typing - that request will update the menu.
        if (controller.signal.aborted) return;
        // Otherwise fall back to matching the served cities locally.
        console.error("Error fetching place suggestions:", error);
      }
      if (query !== this.typedPlaceQuery) return;
      this.places = places;
      this.placesSearching = false;
      this.suggestController = null;
      this.selectFirstLocation();
    },
    onQueryInput() {
      this.openPanel = "query";
      this.activeIndex = -1;
    },
    onKeydown(panel, event) {
      const count = this.openPanel === panel ? this.activeOptions.length : 0;
      switch (event.key) {
        case "ArrowDown":
        case "ArrowUp": {
          event.preventDefault();
          if (this.openPanel !== panel) {
            this.open(panel);
            return;
          }
          if (!count) return;
          const step = event.key === "ArrowDown" ? 1 : -1;
          this.activeIndex = (this.activeIndex + step + count) % count;
          this.scrollActiveIntoView(panel);
          break;
        }
        case "Enter":
          event.preventDefault();
          if (count && this.activeIndex >= 0) {
            const option = this.activeOptions[this.activeIndex];
            if (panel === "location") this.chooseLocation(option);
            else this.chooseQuery(option);
          } else if (panel === "location") {
            this.openPanel = null;
            this.$refs.queryInput.focus();
          } else {
            this.submit();
          }
          break;
        case "Escape":
          this.openPanel = null;
          break;
        default:
      }
    },
    scrollActiveIntoView(panel) {
      this.$nextTick(() => {
        const el = document.getElementById(this.optionId(panel, this.activeIndex));
        if (el) el.scrollIntoView({ block: "nearest" });
      });
    },

    // --- location -------------------------------------------------------------
    chooseLocation(option) {
      if (option.kind === "locate") {
        this.useMyLocation();
        return;
      }
      if (option.kind === "place") {
        this.setPlace(option.place);
        return;
      }
      this.setCity(option.kind === "city" ? option.city : null);
    },
    setCity(city) {
      this.cancelSuggest();
      this.area = city ? city.name : "";
      this.selectedCityId = city ? city.city_id : null;
      this.selectedPlace = null;
      saveLastCity(city);
      this.afterLocationPicked();
    },
    /** A city or locality from the gazetteer (GET /places/...). */
    setPlace(place) {
      this.cancelSuggest();
      this.area = place.label;
      // Null for a city we do not serve yet; the search then goes by name.
      this.selectedCityId = place.city_id || null;
      this.selectedPlace = place;
      saveLastCity(placeToLastCity(place));
      this.afterLocationPicked();
    },
    afterLocationPicked() {
      this.places = [];
      this.placesSearching = false;
      this.openPanel = null;
      // Like Practo: once the place is set, carry on with what to search for.
      this.$nextTick(() => this.$refs.queryInput.focus());
    },
    clearCity() {
      this.cancelSuggest();
      this.area = "";
      this.selectedCityId = null;
      this.selectedPlace = null;
      this.places = [];
      this.placesSearching = false;
      saveLastCity(null);
      this.$refs.locationInput.focus();
      this.open("location");
    },
    async useMyLocation() {
      if (this.locating) return;
      this.locating = true;
      try {
        const position = await getCurrentPosition();
        // The locality the user is in; served cities only as a fallback for
        // when the gazetteer has not been imported.
        const place = await fetchNearestPlace(position);
        const city = place ? null : await fetchNearestCity(position);
        if (place) {
          this.setPlace(place);
        } else if (city) {
          this.setCity(city);
        } else {
          this.openPanel = null;
          this.message = this.$t("city_location_not_served");
        }
      } catch (error) {
        // `reason` is set by getCurrentPosition; anything else is an API failure.
        this.openPanel = null;
        this.message = this.$t(`city_location_${error.reason || "failed"}`);
      } finally {
        this.locating = false;
      }
    },
    // Turns whatever is in the city box into a city filter, or null for none.
    // Searching never creates a city - an unrecognised name falls back to the
    // legacy name search.
    resolveSearchCity() {
      const place = this.selectedPlace;
      if (place) {
        return {
          city_id: place.city_id || null,
          name: place.city_name || place.name,
          locality: place.kind === "locality" ? place.name : null,
          place_id: place.place_id
        };
      }
      const name = cleanCityName(this.area);
      if (!name || isAllCities(name)) return null;
      if (this.selectedCityId) return { city_id: this.selectedCityId, name };
      const known = findCityByName(this.cities, name);
      if (known) return { city_id: known.city_id, name: known.name };
      return { city_id: null, name };
    },

    // --- search ---------------------------------------------------------------
    chooseQuery(option) {
      this.text = option.label;
      this.submit();
    },
    submit() {
      const city = this.resolveSearchCity();
      const query = {
        filter: this.text.trim(),
        // `city` (the name) is kept for bookmarked URLs and the legacy area
        // search; city_id is what the API prefers.
        city: city ? city.name : ""
      };
      if (city && city.city_id) query.city_id = city.city_id;
      // Doctors are filtered by the city; the locality is carried for display
      // until clinics have coordinates (docs/location-search.md).
      if (city && city.locality) query.locality = city.locality;
      if (city && city.place_id) query.place_id = city.place_id;
      this.cancelSuggest();
      this.openPanel = null;
      this.$refs.queryInput.blur();
      this.$router.push({ name: "search", query });
    },

    /** Split `label` into [{ text, match }] around the first match of `query`. */
    highlight(label, query) {
      const needle = (query || "").trim().toLowerCase();
      const at = needle ? label.toLowerCase().indexOf(needle) : -1;
      if (at < 0) return [{ text: label, match: false }];
      return [
        { text: label.slice(0, at), match: false },
        { text: label.slice(at, at + needle.length), match: true },
        { text: label.slice(at + needle.length), match: false }
      ].filter(part => part.text);
    }
  }
};
</script>

<style scoped>
.yd-sb {
  --yd-sb-blue: #0e82fd;
  --yd-sb-border: #d9dde4;
  --yd-sb-text: #1f2937;
  --yd-sb-muted: #6b7280;
  --yd-sb-hover: #f2f7ff;
  --yd-sb-height: 56px;
  position: relative;
  display: flex;
  align-items: stretch;
  width: 100%;
  background: #fff;
  border: 1px solid var(--yd-sb-border);
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(16, 24, 40, 0.08);
}

.yd-sb--compact {
  --yd-sb-height: 48px;
  box-shadow: none;
}

.yd-sb__field {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
}

.yd-sb__field--location {
  flex: 0 0 38%;
  border-right: 1px solid var(--yd-sb-border);
}

.yd-sb__field--query {
  flex: 1 1 auto;
}

.yd-sb__icon {
  position: absolute;
  left: 16px;
  font-size: 17px;
  color: var(--yd-sb-muted);
  pointer-events: none;
}

.yd-sb__input {
  width: 100%;
  height: var(--yd-sb-height);
  padding: 0 40px 0 44px;
  border: 0;
  background: transparent;
  font-size: 15px;
  color: var(--yd-sb-text);
  outline: none;
}

.yd-sb__input::placeholder {
  color: #9aa1ad;
}

.yd-sb__field:focus-within {
  box-shadow: inset 0 -2px 0 var(--yd-sb-blue);
}

.yd-sb__clear {
  position: absolute;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #8b929e;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}

.yd-sb__clear:hover {
  background: #5f6672;
}

.yd-sb__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1050;
  width: 100%;
  min-width: 320px;
  max-height: 420px;
  margin: 0;
  padding: 6px 0;
  overflow-y: auto;
  list-style: none;
  background: #fff;
  border: 1px solid var(--yd-sb-border);
  border-radius: 10px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.14);
}

.yd-sb__field--query .yd-sb__menu {
  min-width: 360px;
}

.yd-sb__option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  color: var(--yd-sb-text);
}

.yd-sb__option.is-active {
  background: var(--yd-sb-hover);
}

.yd-sb__option--locate,
.yd-sb__option--all {
  gap: 8px;
  padding: 13px 16px;
  font-weight: 600;
  color: var(--yd-sb-blue);
  border-bottom: 1px solid #edf0f4;
}

.yd-sb__badge {
  display: inline-flex;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eef1f5;
  color: var(--yd-sb-muted);
  font-size: 14px;
}

.yd-sb__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.yd-sb__title {
  overflow: hidden;
  font-size: 15px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.yd-sb__match {
  font-weight: 700;
}

.yd-sb__sub {
  font-size: 12.5px;
  color: var(--yd-sb-muted);
}

.yd-sb__type {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1ad;
}

.yd-sb__heading {
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1ad;
}

.yd-sb__status {
  padding: 12px 16px;
  font-size: 14px;
  color: var(--yd-sb-muted);
}

.yd-sb__status--error {
  color: #c0392b;
}

.yd-sb__submit {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 8px;
  padding: 0 22px;
  border: 0;
  border-radius: 8px;
  background: var(--yd-sb-blue);
  color: #fff;
  font-weight: 600;
}

.yd-sb__submit:hover {
  background: #0b6fd8;
}

.yd-sb__message {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  margin: 0;
  font-size: 13.5px;
  color: #c0392b;
}

/* Phones: stack the fields and show an explicit Search button. */
@media (max-width: 767.98px) {
  .yd-sb {
    flex-direction: column;
  }

  .yd-sb__field--location {
    flex: none;
    border-right: 0;
    border-bottom: 1px solid var(--yd-sb-border);
  }

  .yd-sb__menu,
  .yd-sb__field--query .yd-sb__menu {
    min-width: 0;
  }

  .yd-sb__submit {
    display: flex;
    height: 46px;
  }

  .yd-sb__message {
    position: static;
    padding: 0 12px 10px;
  }
}
</style>
